import { Info, Warning } from "@mui/icons-material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { createRef } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';

export default function Discovery({ caseInfo, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
        Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap[caseInfo['documents'][docKey]['filename']]['selected']) :
        [];
    const isDiscoveryInProgress = caseInfo && Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.includes('categorize') || doc['analysis_queue']?.includes('discovery') || doc['analysis_queue']?.includes('personal_injury_catchall'));

    var discoveryItems = documentKeysToDisplay.map(documentKey => {
        caseInfo?.['documents'][documentKey]['analysis']?.['discovery']?.forEach((discoveryItem, i) => {
            discoveryItem['id'] = documentKey + i; // Used in UI as keys for use by React framework
            discoveryItem['source_filekey'] = documentKey; // File used by SourceDocumentViewer
        });
        return caseInfo['documents'][documentKey]['analysis']?.['discovery'];
    })
        .filter(discoveryArr => discoveryArr) // the ?. above can cause null entries; filter them
        .reduce((accumulator, discoveryArr) => accumulator.concat(discoveryArr), [])
        .filter(discoveryItem => isValidDiscoveryItem(discoveryItem));

    if (discoveryItems.length === 0) {
        return (
            <Box sx={{
                p: 3,
                textAlign: 'center',
                mt: 8,
                mb: 8
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'var(--text-secondary)',
                        fontWeight: 400,
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                    }}
                >
                    Potential evidence will be automatically extracted from uploaded Discovery documents such as Depositions, Interrogatories and displayed here.
                </Typography>
            </Box>
        )
    }

    var tableBodyRows = discoveryItems
        .map(discoveryItem => {
            const documentKey = discoveryItem.source_filekey;
            const docInfo = caseInfo.documents[documentKey];
            const filename = docInfo?.['filename'];

            return (
                <TableRow
                    key={discoveryItem['id']}
                    onClick={() => {
                        setDisplayedDocument({ 'key': discoveryItem['source_filekey'], 'page': discoveryItem['page'] })
                    }}
                    className={discoveryItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                    sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'var(--surface-color)',
                            transform: 'translateY(-1px)',
                            boxShadow: 'var(--shadow-subtle)'
                        },
                        '&:hover .source-view-icon': {
                            visibility: 'visible',
                            opacity: 0.7
                        }
                    }}
                >
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderEvidenceWithReview(discoveryItem)}
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderFieldWithReview(discoveryItem, 'reason')}
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                        textAlign: 'center'
                    }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                                component="span"
                                title="Click to view source document"
                                sx={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontSize: '0.9rem',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': discoveryItem['source_filekey'],
                                        'page': discoveryItem.source?.page || discoveryItem.page || 1
                                    });
                                }}
                            >
                                {filename} {(discoveryItem.source?.page || discoveryItem.page) && `(page ${discoveryItem.source?.page || discoveryItem.page})`}
                            </Typography>
                            <IconButton
                                className="source-view-icon"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': discoveryItem['source_filekey'],
                                        'page': discoveryItem.source?.page || discoveryItem.page || 1
                                    });
                                }}
                                sx={{
                                    padding: '2px',
                                    visibility: 'hidden',
                                    opacity: 0,
                                    transition: 'opacity 0.2s, visibility 0.2s',
                                    '&:hover': {
                                        opacity: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                                title="Click to view source document"
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        });

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isDiscoveryInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle"
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Discovery documents are being analyzed for evidence...
                            </Typography>
                        </Box>
                    )}
                </Box>
                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Discovery" />
            </Box>
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="discovery table"
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "150px"
                        }}>
                            Evidence
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Notes
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        </Box>
    );

    function isValidDiscoveryItem(discoveryItem) {
        return discoveryItem['verbatim'] || discoveryItem['source']?.verbatim;
    }

    function renderEvidenceWithReview(discoveryItem) {
        const needsReview = discoveryItem.needs_review;
        const caveats = discoveryItem.caveats;

        const evidenceContent = (
            <Typography component='div' fontWeight={200}>
                "{discoveryItem.source?.before_verbatim || discoveryItem.before_verbatim}
                <Box fontWeight='fontWeightMedium' display='inline'>
                    {' ' + discoveryItem.source?.verbatim || discoveryItem.verbatim + ' '}
                </Box>
                {discoveryItem.source?.after_verbatim || discoveryItem.after_verbatim}"
            </Typography>
        );

        // TODO: needs_review and caveats need to be tested with current prompt.
        // Collect all warning messages
        const warningMessages = [];

        if (needsReview && (needsReview.verbatim || needsReview.before_verbatim || needsReview.after_verbatim)) {
            warningMessages.push(
                <Box key="needs-review" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Warning sx={{ color: 'var(--error-color)', fontSize: '16px', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'var(--error-color)', fontSize: '12px', fontWeight: 500 }}>
                        {needsReview.verbatim || needsReview.before_verbatim || needsReview.after_verbatim}
                    </Typography>
                </Box>
            );
        }

        if (caveats && (caveats.verbatim || caveats.before_verbatim || caveats.after_verbatim)) {
            warningMessages.push(
                <Box key="caveats" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Info sx={{ color: 'var(--info-color)', fontSize: '16px', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'var(--info-color)', fontSize: '12px', fontWeight: 500 }}>
                        {caveats.verbatim || caveats.before_verbatim || caveats.after_verbatim}
                    </Typography>
                </Box>
            );
        }

        if (warningMessages.length > 0) {
            return (
                <Box>
                    {warningMessages}
                    {evidenceContent}
                </Box>
            );
        }

        return evidenceContent;
    }

    function renderFieldWithReview(discoveryItem, fieldName, customValue = null) {
        const needsReview = discoveryItem.needs_review?.[fieldName];
        const caveats = discoveryItem.caveats?.[fieldName];
        const fieldValue = customValue !== null ? customValue : (discoveryItem[fieldName] || '');

        // Collect all warning messages
        const warningMessages = [];

        if (needsReview) {
            warningMessages.push(
                <Box key="needs-review" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Warning sx={{ color: 'var(--error-color)', fontSize: '16px', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'var(--error-color)', fontSize: '12px', fontWeight: 500 }}>
                        {needsReview}
                    </Typography>
                </Box>
            );
        }

        if (caveats) {
            warningMessages.push(
                <Box key="caveats" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Info sx={{ color: 'var(--info-color)', fontSize: '16px', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'var(--info-color)', fontSize: '12px', fontWeight: 500 }}>
                        {caveats}
                    </Typography>
                </Box>
            );
        }

        if (warningMessages.length > 0) {
            return (
                <Box>
                    {warningMessages}
                    <Typography sx={{ color: 'var(--text-primary)' }}>
                        {fieldValue}
                    </Typography>
                </Box>
            );
        }

        return fieldValue;
    }
}
